import React, { useState, useMemo } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import Swal from "sweetalert2";
import axios from "axios";
import {
  checkUserAcc,
  validatePassword,
  validateEmail,
  dobFormat,
} from "../../common/signup";
import firebase, {
  functionBaseUrl,
  axiosWithToken,
} from "../../common/firebase";
import { listOfSubscriptions } from "../../common/envConfig";
import { gtmEvent } from "../../utils/gtmEvent";

function PlansSignupComponent({ finishSignUp, back, plan }) {
  const [userFirstName, setUserFirstName] = useState("");
  const [userLastName, setUserLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [tosAgree, setTosAgree] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const href = useMemo(() => window.location.href, []);
  const isTrial = useMemo(() => href.includes("trial=true"), [href]);
  const isPlayersFlow = useMemo(
    () => href.includes("plansType=players"),
    [href]
  );

  const normalizedEmail = email.toLowerCase().trim();
  const trimmedFirstName = userFirstName.trim();
  const trimmedLastName = userLastName.trim();
  const fullName = `${trimmedFirstName} ${trimmedLastName}`;
  const isFreePlan = plan?.includes("free");

  // -------------------- ALERT HELPERS --------------------

  const showWarning = (msg) =>
    Swal.fire({
      title: `<p style="font-size:70%;">${msg}</p>`,
      icon: "warning",
      confirmButtonText: "Ok",
      allowOutsideClick: true,
    });

  // -------------------- VALIDATION --------------------

  const validateForm = async () => {
    if (!trimmedFirstName) {
      await showWarning("Please enter your First Name");
      return false;
    }

    if (!trimmedLastName) {
      await showWarning("Please enter your Last Name");
      return false;
    }

    if (!normalizedEmail) {
      await showWarning("Please enter your Email");
      return false;
    }

    if (!validateEmail(normalizedEmail)) {
      await showWarning("Please enter a valid Email");
      return false;
    }

    if (!dateOfBirth) {
      await showWarning("Please enter your Date of Birth");
      return false;
    }

    const minAgeDate = new Date();
    minAgeDate.setFullYear(minAgeDate.getFullYear() - 13);
    if (new Date(dateOfBirth) > minAgeDate) {
      await Swal.fire({
        title:
          '<p style="font-size:70%;">Sorry, you are not eligible for a Sportsbox.ai account at this time.</p>',
        icon: "warning",
        confirmButtonText: "Ok",
        allowOutsideClick: false,
      });
      await firebase.auth().signOut();
      window.location.reload();
      return false;
    }

    if (!password) {
      await showWarning("Please enter Password");
      return false;
    }

    if (!validatePassword(password)) {
      await Swal.fire({
        title: '<p style="font-size:70%;">Password must contain:</p>',
        html: `
        <ul>
          <li>• At least 8 characters</li>
          <li>• At least one number</li>
          <li>• A special character (e.g., !@#$%&*)</li>
        </ul>
      `,
        icon: "warning",
        confirmButtonText: "Ok",
        allowOutsideClick: true,
      });
      return false;
    }

    if (!confirmPassword) {
      await showWarning("Please enter Password Confirmation");
      return false;
    }

    if (password !== confirmPassword) {
      await showWarning("Passwords do not match");
      return false;
    }

    if (!tosAgree) {
      await showWarning("Please accept Terms of Service and Privacy Policy");
      return false;
    }

    return true;
  };

  // -------------------- NEW USER FLOW --------------------

  const registerNewUser = async () => {
    const creds = await firebase
      .auth()
      .createUserWithEmailAndPassword(normalizedEmail, password);

    if (isPlayersFlow) {
      gtmEvent({
        event: "click_get_started",
        plan,
        uid: creds.user.uid,
        page: href,
      });
    }

    if (!isFreePlan) {
      await axios(`${functionBaseUrl}/api/userEmailVerf`, {
        method: "PATCH",
        data: { id: creds.user.uid, status: true },
      });
    } else {
      await creds.user.sendEmailVerification();
    }

    await axios(`${functionBaseUrl}/api/userMobile`, {
      method: "POST",
      data: {
        uid: creds.user.uid,
        firstName: trimmedFirstName,
        lastName: trimmedLastName,
        userName: fullName,
        email: normalizedEmail,
        phone: phoneNumber,
        dob: dobFormat(dateOfBirth),
        role: "Sportsbox-Instructor",
        subscriptionType: listOfSubscriptions.STUDENT_FREE,
        goals: [],
        averageScore: 0,
        weeklyPractices: 0,
        coachFacility: [
          {
            name: "",
            address: "",
            students: 0,
            lessons: 0,
            environment: [],
          },
        ],
        certification: [],
        gender: "",
        height: "",
        hipSize: "7.2",
        dominantHand: "",
        picture: "",
        coachAddress: { city: "", state: "", country: "" },
        userConsent: true,
      },
    });

    if (isFreePlan) {
      await axios(`${functionBaseUrl}/api/updateUserNY/${creds.user.uid}`, {
        method: "POST",
        data: {
          userInfo: { subscriptionType: listOfSubscriptions.STUDENT_FREE },
        },
      });
    }

    await firebase.auth().updateCurrentUser(creds.user);
    const token = await creds.user.getIdToken(true);
    localStorage.setItem("currUser", token);
    return creds.user;
  };

  // -------------------- EXISTING USER FLOW --------------------

  const handleExistingUser = async () => {
    const res = await axios(`${functionBaseUrl}/api/userInAuth`, {
      method: "POST",
      data: { email: normalizedEmail },
    });

    const type = res.data?.subscriptionType;

    if (!type || type.includes("free")) {
      finishSignUp(res.data.id, normalizedEmail);
      return;
    }

    await Swal.fire({
      title:
        '<p style="font-size:70%;">An account already exists with this email, please log in to continue.</p>',
      icon: "warning",
      confirmButtonText: "Ok",
      showCancelButton: true,
      allowOutsideClick: false,
    }).then((result) => {
      if (result.isConfirmed) {
        window.location.href = href.replace(
          "sign-up",
          "sign-in?redirectTo=/plans"
        );
      }
    });
  };

  // -------------------- SUBMIT --------------------

  const handleSignUp = async () => {
    const valid = await validateForm();
    if (!valid) return;
    setIsLoading(true);

    const userDoesNotExist = await checkUserAcc(normalizedEmail);

    if (userDoesNotExist) {
      try {
        const user = await registerNewUser();
        if (!user) return;
        setIsLoading(false);
        finishSignUp(user.uid, normalizedEmail, fullName);
      } catch (err) {
        setIsLoading(false);
        showWarning(err.message || "Sign up failed, please try again.");
      }
    } else {
      setIsLoading(false);

      await Swal.fire({
        title:
          '<p style="font-size:70%;">An account already exists with this email, would you like to log in?</p>',
        icon: "warning",
        confirmButtonText: "Log In",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.href = href.replace("sign-up", "sign-in");
        }
      });

      return;
    }
  };

  // -------------------- UI --------------------

  return (
    <Grid container className="plan-checkout">
      <Grid item xs={12} md={2} />

      <Grid item xs={12} md={8}>
        <Grid container>
          <Grid item xs={12} className="plans-step-back">
            <Button onClick={back}>
              <ArrowBack />
            </Button>
          </Grid>

          {isTrial && (
            <Grid item xs={12} className="plans-signup-caption-trial">
              14-day Free Trial
            </Grid>
          )}

          <Grid item xs={12} className="plans-signup-caption">
            Account
          </Grid>

          <Grid item xs={6} className="plans-signup-form-element">
            <TextField
              fullWidth
              required
              label="First Name"
              value={userFirstName}
              onChange={(e) => setUserFirstName(e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={6} className="plans-signup-form-element">
            <TextField
              fullWidth
              required
              label="Last Name"
              value={userLastName}
              onChange={(e) => setUserLastName(e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} className="plans-signup-form-element">
            <TextField
              InputLabelProps={{ shrink: true }}
              required
              fullWidth
              variant="outlined"
              label="Date of Birth"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
            />
          </Grid>

          <Grid item xs={12} className="plans-signup-form-element">
            <TextField
              fullWidth
              label="Phone Number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={12} className="plans-signup-form-element">
            <TextField
              fullWidth
              required
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
            />
          </Grid>

          <Grid item xs={6} className="plans-signup-form-element">
            <FormControl fullWidth variant="outlined">
              <InputLabel>Password *</InputLabel>
              <OutlinedInput
                label="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>
          </Grid>

          <Grid item xs={6} className="plans-signup-form-element">
            <FormControl fullWidth variant="outlined">
              <InputLabel>Confirm Password *</InputLabel>
              <OutlinedInput
                label="Confirm Password"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </FormControl>

            {confirmPassword && password !== confirmPassword && (
              <Typography style={{ color: "red" }}>
                The passwords do not match
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} className="plans-signup-form-element">
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={tosAgree}
                  onChange={(e) => setTosAgree(e.target.checked)}
                />
              }
              label={
                <>
                  <span>I agree to the </span>
                  <Link
                    onClick={() =>
                      window.open(href.replace("sign-up", "terms-of-service"))
                    }
                  >
                    Terms of Service
                  </Link>
                  {" and "}
                  <Link
                    onClick={() =>
                      window.open(href.replace("sign-up", "privacy"))
                    }
                  >
                    Privacy Policy
                  </Link>
                </>
              }
            />
          </Grid>

          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Button
              className="plan-button"
              disabled={isLoading}
              variant="contained"
              onClick={handleSignUp}
            >
              {isLoading && (
                <>
                  <CircularProgress size="1.2rem" />
                  &nbsp;&nbsp;
                </>
              )}
              Get started
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12} md={2} />
    </Grid>
  );
}

export default PlansSignupComponent;
